import type { Question, QuestionType, Section, SurveyItem } from '@voice2me/shared';
import { newId } from '../api.js';
import { RESPONSE_TYPE_LABELS, matchResponseType } from './responseTypes.js';

/** A row that could not be turned into a question, with the 1-based spreadsheet row number. */
export type ImportIssue = { row: number; message: string };

/**
 * The result of reading a spreadsheet: the content built (questions, optionally grouped into
 * sections), the number of questions across it (for messaging), plus anything that was skipped.
 */
export type ImportOutcome = {
  items: SurveyItem[];
  questionCount: number;
  issues: ImportIssue[];
  skipped: number;
};

export type ImportOptions = {
  /** When true, column 2 is read as a section title and the response type moves to column 3. */
  hasSectionColumn?: boolean;
};

// Kept in step with the bounds in `ratingQuestionSchema` (packages/shared/src/survey.ts) so the
// friendly message here fires before the schema rejects the survey with a rawer one.
const RATING_MIN = 0;
const RATING_MAX = 10;
const MAX_PROMPT_LENGTH = 500;
const MAX_SECTION_TITLE_LENGTH = 200;
const MAX_CHOICE_OPTIONS = 50;
const TYPE_LIST = RESPONSE_TYPE_LABELS.join(', ');

/** Builds a plain open-text question, used for the "paste a list" import path. */
export function promptToOpenText(prompt: string): Question {
  return { id: newId('q'), prompt: prompt.trim(), required: true, type: 'open_text', maxLength: 2000 };
}

/**
 * Converts the raw cell grid from a spreadsheet into survey content.
 *
 * Default row shape (see the in-app format guide): column 1 is the question text, column 2 is the
 * response type. `Scale` reads a low/high integer from columns 3–4 and optional labels from 5–6;
 * `Multiple Choice` treats every column from 3 onward as an option. An optional header row and
 * fully blank rows are ignored; anything malformed is reported as an issue rather than silently
 * dropped, so the person importing can see exactly which rows need fixing.
 *
 * When `hasSectionColumn` is set, column 2 is instead read as a section title and everything from
 * the response type onward shifts one column right. A blank section cell leaves the question at the
 * top level; consecutive rows sharing a section title are grouped into one section, in row order.
 */
export function rowsToQuestions(rows: string[][], options: ImportOptions = {}): ImportOutcome {
  const hasSectionColumn = options.hasSectionColumn ?? false;
  const grouper = new SectionGrouper();
  const issues: ImportIssue[] = [];
  let skipped = 0;

  rows.forEach((rawRow, index) => {
    const rowNumber = index + 1; // 1-based to match the spreadsheet's own row numbering.
    const row = rawRow.map((cell) => (cell ?? '').trim());

    if (row.every((cell) => cell === '')) return; // Blank spacer row — ignore quietly.

    // Drop the section column so the rest of the parsing sees the familiar question/type/... shape.
    const sectionTitle = hasSectionColumn ? (row[1] ?? '') : '';
    const questionRow = hasSectionColumn ? [row[0] ?? '', ...row.slice(2)] : row;

    if (index === 0 && looksLikeHeader(questionRow, sectionTitle)) return;

    const prompt = questionRow[0] ?? '';
    if (!prompt) {
      issues.push({ row: rowNumber, message: 'No question text in column 1.' });
      skipped += 1;
      return;
    }
    if (prompt.length > MAX_PROMPT_LENGTH) {
      issues.push({ row: rowNumber, message: `Question text is too long (max ${MAX_PROMPT_LENGTH} characters).` });
      skipped += 1;
      return;
    }
    if (sectionTitle.length > MAX_SECTION_TITLE_LENGTH) {
      issues.push({
        row: rowNumber,
        message: `Section title is too long (max ${MAX_SECTION_TITLE_LENGTH} characters).`,
      });
      skipped += 1;
      return;
    }

    const typeCell = questionRow[1] ?? '';
    const type = matchResponseType(typeCell);
    if (!type) {
      const typeColumn = hasSectionColumn ? 3 : 2;
      issues.push({
        row: rowNumber,
        message: typeCell
          ? `Unrecognised response type "${typeCell}". Use ${TYPE_LIST}.`
          : `No response type in column ${typeColumn}. Use ${TYPE_LIST}.`,
      });
      skipped += 1;
      return;
    }

    const built = buildQuestion(type, prompt, questionRow);
    if ('error' in built) {
      issues.push({ row: rowNumber, message: built.error });
      skipped += 1;
      return;
    }
    grouper.place(built.question, sectionTitle);
  });

  return { items: grouper.items, questionCount: grouper.questionCount, issues, skipped };
}

/**
 * Assembles questions into the ordered content list, opening a new section whenever a run of rows
 * carries a section title different from the one currently open. A blank title places the question
 * at the top level and closes any open section.
 */
class SectionGrouper {
  readonly items: SurveyItem[] = [];
  questionCount = 0;
  private open: Section | null = null;

  place(question: Question, sectionTitle: string): void {
    this.questionCount += 1;
    if (!sectionTitle) {
      this.items.push(question);
      this.open = null;
      return;
    }
    if (!this.open || this.open.title !== sectionTitle) {
      this.open = { kind: 'section', id: newId('sec'), title: sectionTitle, questions: [] };
      this.items.push(this.open);
    }
    this.open.questions.push(question);
  }
}

/** A header row is optional; recognise it so we don't try to import the labels as a question. */
function looksLikeHeader(questionRow: string[], sectionCell: string): boolean {
  const first = (questionRow[0] ?? '').toLowerCase();
  const second = (questionRow[1] ?? '').toLowerCase();
  return (
    first === 'question' ||
    second === 'response type' ||
    second === 'response' ||
    sectionCell.toLowerCase() === 'section'
  );
}

type BuildResult = { question: Question } | { error: string };

/** Turns one validated prompt + type into a question, reading the type-specific extra columns. */
function buildQuestion(type: QuestionType, prompt: string, row: string[]): BuildResult {
  const base = { id: newId('q'), prompt, required: true };

  switch (type) {
    case 'open_text':
      return { question: { ...base, type, maxLength: 2000 } };

    case 'boolean':
      return { question: { ...base, type } };

    case 'rating': {
      const min = parseInteger(row[2]);
      const max = parseInteger(row[3]);
      if (min === null || max === null) {
        return { error: 'Scale needs whole numbers in columns 3 and 4 for the low and high ends.' };
      }
      if (min < RATING_MIN || max > RATING_MAX) {
        return { error: `Scale values must be between ${RATING_MIN} and ${RATING_MAX}.` };
      }
      if (max <= min) {
        return { error: 'Scale high value (column 4) must be greater than the low value (column 3).' };
      }
      const minLabel = row[4] || undefined;
      const maxLabel = row[5] || undefined;
      return {
        question: {
          ...base,
          type,
          min,
          max,
          ...(minLabel ? { minLabel } : {}),
          ...(maxLabel ? { maxLabel } : {}),
        },
      };
    }

    case 'multiple_choice': {
      const labels = row.slice(2).filter((label) => label !== '');
      if (labels.length < 2) {
        return { error: 'Multiple choice needs at least two options (columns 3 onward).' };
      }
      if (labels.length > MAX_CHOICE_OPTIONS) {
        return { error: `Multiple choice allows at most ${MAX_CHOICE_OPTIONS} options.` };
      }
      return {
        question: {
          ...base,
          type,
          allowMultiple: false,
          options: labels.map((label) => ({ id: newId('opt'), label })),
        },
      };
    }
  }
}

/** Parses a strict whole number, rejecting decimals, blanks, and anything non-numeric. */
function parseInteger(value: string | undefined): number | null {
  const trimmed = (value ?? '').trim();
  return /^-?\d+$/.test(trimmed) ? Number(trimmed) : null;
}
